import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  FaUniversity,
  FaMapMarkerAlt,
  FaPhone,
  FaCity,
  FaUser,
  FaMoon,
  FaSun,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SummaryApi from '../common/index';

const CollegeManagement = ({ onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    college_name: '',
    address: '',
    city: '',
    contact_number: '',
    contact_person: '',
    is_active: false,
  });
  const [editingId, setEditingId] = useState(null);
const initialTheme = sessionStorage.getItem('admin-theme') === 'dark';
const [isDarkMode, setIsDarkMode] = useState(initialTheme);
if (initialTheme) {
  document.documentElement.classList.add('dark');
} else {
  document.documentElement.classList.remove('dark');
}
  // Extract ID from URL and fetch college data if editing
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('id');

    if (id) {
      setEditingId(id);
      fetchCollegeById(id);
    }
  }, [location]);

  const fetchCollegeById = async (id) => {
    try {
      const response = await fetch(SummaryApi.CollegeById.url, {
        method: SummaryApi.CollegeById.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ college_id: id }),
      });
      const data = await response.json();

      if (data.status && data.data) {
        setFormData({
          college_name: data.data.college_name || '',
          address: data.data.address || '',
          city: data.data.city || '',
          contact_number: data.data.contact_number || '',
          contact_person: data.data.contact_person || '',
          is_active: data.data.is_active === 1,
        });
      } else {
        toast.error(data.message || 'Failed to fetch college data');
      }
    } catch (error) {
      console.error('Error fetching college:', error);
      toast.error('Error fetching college data');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'contact_number') {
      const numeric = value.replace(/[^0-9]/g, '');
      if (numeric.length <= 10) {
        setFormData((prev) => ({ ...prev, [name]: numeric }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const toggleActive = () => {
    setFormData((prev) => ({ ...prev, is_active: !prev.is_active }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      is_active: formData.is_active ? 1 : 0,
    };

    try {
      if (editingId) {
        // === UPDATE COLLEGE ===
        const res = await fetch(
          SummaryApi.Collegeupdate?.url || '/api/colleges',
          {
            method: SummaryApi.Collegeupdate?.method || 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...payload, college_id: editingId }),
          }
        );
        const data = await res.json();

        if (data.status) {
          toast.success('College updated successfully!');

          // Close modal/tab only on update
          if (typeof onClose === 'function') {
            onClose();
          }
        } else {
          toast.error(data.message || 'Failed to update college');
        }
      } else {
        // === CREATE COLLEGE ===
        const res = await fetch(SummaryApi.Collegecreate.url, {
          method: SummaryApi.Collegecreate.method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();

        if (data.status) {
          toast.success('College created successfully!');
          resetForm();

          // Navigate to list only on create
          navigate('/admin-panel/college-list');
        } else {
          toast.error(data.message || 'Failed to create college');
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Something went wrong');
    }
  };

  const resetForm = () => {
    setFormData({
      college_name: '',
      address: '',
      city: '',
      contact_number: '',
      contact_person: '',
      is_active: false,
    });
    setEditingId(null);
  };



  // Keep theme in sync when changed from other pages/tabs
  useEffect(() => {
    const handleThemeChange = () => {
      const isDark = sessionStorage.getItem('admin-theme') === 'dark';
      setIsDarkMode(isDark);
      document.documentElement.classList.toggle('dark', isDark);
    };

    window.addEventListener('storage', handleThemeChange);
    window.addEventListener('themeChanged', handleThemeChange);
    window.addEventListener('adminThemeToggled', handleThemeChange);

    return () => {
      window.removeEventListener('storage', handleThemeChange);
      window.removeEventListener('themeChanged', handleThemeChange);
      window.removeEventListener('adminThemeToggled', handleThemeChange);
    };
  }, []);






  return (
    <div className={`min-h-screen p-4 sm:p-6 transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-white'
    }`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8 flex justify-between items-center">
          <h1 className={`text-2xl text-center sm:text-3xl font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            College Management
          </h1>
          

        </div>

        {/* Form Card */}
        <div className={`rounded-xl shadow-lg p-5 sm:p-6 mb-6 sm:mb-8 border transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800 border-gray-600 shadow-gray-900/50' : 'bg-white border-gray-200'
        }`}>
          <h2 className={`text-lg sm:text-xl font-semibold transition-colors duration-300 mb-4 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            {editingId ? 'Update College' : 'Create New College'}
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* College Name */}
              <div className="relative">
                <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  College Name
                </label>
                <div className="relative">
                  <FaUniversity className={`absolute left-2 top-3.5 transition-colors duration-300 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <input
                    type="text"
                    name="college_name"
                    value={formData.college_name}
                    onChange={handleChange}
                    className={`block w-full border rounded-md p-2 pl-8 focus:outline-none focus:ring-2 transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500' 
                        : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    required
                  />
                </div>
              </div>

              {/* Address */}
              <div className="relative">
                <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Address
                </label>
                <div className="relative">
                  <FaMapMarkerAlt className={`absolute left-2 top-3.5 transition-colors duration-300 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={`block w-full border rounded-md p-2 pl-8 focus:outline-none focus:ring-2 transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500' 
                        : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    required
                  />
                </div>
              </div>

              {/* City */}
              <div className="relative">
                <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  City
                </label>
                <div className="relative">
                  <FaCity className={`absolute left-2 top-3.5 transition-colors duration-300 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={`block w-full border rounded-md p-2 pl-8 focus:outline-none focus:ring-2 transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500' 
                        : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    required
                  />
                </div>
              </div>

              {/* Contact Number */}
              <div className="relative">
                <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Contact Number
                </label>
                <div className="relative">
                  <FaPhone className={`absolute left-2 top-3.5 transition-colors duration-300 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <input
                    type="text"
                    name="contact_number"
                    value={formData.contact_number}
                    onChange={handleChange}
                    className={`block w-full border rounded-md p-2 pl-8 focus:outline-none focus:ring-2 transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500' 
                        : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    required
                    pattern="[0-9]{10}"
                    maxLength="10"
                    title="Please enter a valid 10-digit phone number"
                  />
                </div>
              </div>

              {/* Contact Person */}
              <div className="relative">
                <label className={`block text-sm font-medium mb-1 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Contact Person
                </label>
                <div className="relative">
                  <FaUser className={`absolute left-2 top-3.5 transition-colors duration-300 ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-600'
                  }`} />
                  <input
                    type="text"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleChange}
                    className={`block w-full border rounded-md p-2 pl-8 focus:outline-none focus:ring-2 transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500' 
                        : 'border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    required
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center space-x-3 mt-6 md:mt-0">
                <label className={`text-sm font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>Active</label>
                <div
                  onClick={toggleActive}
                  className={`relative inline-flex items-center h-6 rounded-full w-11 cursor-pointer transition-colors ${
                    formData.is_active ? 'bg-green-600' : 'bg-red-600'
                  }`}
                >
                  <span
                    className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                      formData.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </div>
                <span className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {formData.is_active ? 'Yes' : 'No'}
                </span>
              </div>
            </div>

            <div className="mt-6 flex gap-2 justify-end">
 <button
                type="submit"
                className={`px-4 py-3 rounded-md text-white font-medium transition-colors duration-200 ${
                  isDarkMode
                    ? 'bg-[#1C3664] hover:bg-blue-700 shadow-lg shadow-blue-600/30'
                    : 'bg-[#1C3664] hover:bg-blue-800 shadow-lg shadow-blue-700/30'
                }`}
              >
                {editingId ? 'Update College' : 'Create College'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CollegeManagement;